import React, { useEffect, useState, useCallback } from "react";
import {
  getFirestore,
  getDoc,
  updateDoc,
  doc,
  setDoc,
  increment,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { fetchDraftereyeData, FeedbackDisplay } from "utils/draftereye";
import DraftCardButton from "components/DraftCardButton";
import Login from "components/Login";
import { motion } from "framer-motion";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Audio } from "react-loader-spinner";
import { GameModeEnum, GameStateEnum, GameStatusEnum } from "interfaces/game";
import {
  FREE_DAILY_LIMIT,
  isProOrHigher,
  PRO_FEATURES_ENABLED,
} from "interfaces/user";
import { setGame } from "stores/game/GameSlice";
import { setLobby } from "stores/lobby/LobbySlice";
import ShareButton from "components/ShareButton";
import { prorbabilityToPercentage } from "utils/numbers";
import { Adsense } from "@ctrl/react-adsense";
import { AdsenseAccount } from "constants/config";
import { showAds } from "utils/ads";
import { isMobile } from "react-device-detect";
import { openCheckout } from "utils/stripe";

function Results({ db, onEditChampions }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userReducer.user);
  const userUid = user?.uid;
  const game = useSelector((state) => state.gameReducer.game, shallowEqual);
  const lobby = useSelector((state) => state.lobbyReducer.lobby);
  const characterOrder = ["top", "jungle", "mid", "bottom", "support"];

  const userTeam =
    game?.mode === GameModeEnum.SIMULATION
      ? game?.teams.find((team) => team.userId === "player1")
      : game?.teams.find((team) => team.userId === userUid);
  const opponentTeam =
    game?.mode === GameModeEnum.SIMULATION
      ? game?.teams.find((team) => team.userId === "player2")
      : game?.teams.find((team) => team.userId !== userUid);
  const blueSideTeam =
    game?.mode === GameModeEnum.SIMULATION
      ? game?.teams.find((team) => team.userId === "player1")
      : game?.teams.find((team) => team.userId === game?.players[0]);
  const opponentId = game?.players.filter(
    (playerId) =>
      playerId !==
      (game?.mode === GameModeEnum.SIMULATION ? "player1" : userUid),
  )[0];

  const [opponentUsername, setOpponentUsername] = useState("");

  const userCharacters = userTeam
    ? characterOrder.map((position) => userTeam.team[position])
    : [];
  const opponentCharacters = opponentTeam
    ? characterOrder.map((position) => opponentTeam.team[position])
    : [];
  const [percentages, setPercentages] = useState([]);

  const userIsNotInGame = game?.players && !game?.players.includes(userUid);

  const [draftereyeData, setDraftereyeData] = useState();
  const [authError, setAuthError] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const allCharacters = [...userCharacters, ...opponentCharacters];
  const isUserBlueSide =
    game?.mode === GameModeEnum.SIMULATION
      ? true
      : userTeam?.userId === game?.players[0];

  const isSimulation = game?.mode === GameModeEnum.SIMULATION;
  const cardUserUsername = isSimulation ? "Blue team" : user?.username || "You";
  const cardOpponentUsername = isSimulation
    ? "Red team"
    : opponentUsername || "Opponent";

  useEffect(() => {
    const fetchOpponentUsername = async () => {
      const dbTmp = getFirestore();
      const userDoc = doc(dbTmp, "users", opponentId);
      const userSnapshot = await getDoc(userDoc);

      if (userSnapshot.exists()) {
        setOpponentUsername(userSnapshot.data().username);
      } else {
        console.error("No user? found for opponent");
      }
    };
    if (opponentId && game.mode !== GameModeEnum.SIMULATION) {
      fetchOpponentUsername();
    }
  }, [game?.players, opponentId]);

  const leaveGame = useCallback(() => {
    dispatch(setGame(null));
    dispatch(setLobby(null));
  }, [dispatch]);

  // When user logs in after seeing auth error, retry the API call
  useEffect(() => {
    if (authError && userUid) {
      setAuthError(false);
      setRetryCount((c) => c + 1);
    }
  }, [userUid, authError]);

  useEffect(() => {
    // Only fetch when winner is not yet determined
    if (game.winner !== null) return;

    const fetchWinner = async () => {
      // We only want to fetch percentages if the game isn't finished, i.e if there wasn't any timer miss
      if (
        game.status !== GameStatusEnum.MISSED_TIMER &&
        game.status !== GameStatusEnum.SURREND
      ) {
        // Call API to determine the winner
        try {
          const jsonData = await fetchDraftereyeData(allCharacters);
          setDraftereyeData(jsonData);

          const percentagesLoc = [
            prorbabilityToPercentage(jsonData.p_team1_wins),
            prorbabilityToPercentage(jsonData.p_team2_wins),
          ];
          setPercentages(percentagesLoc);

          if (game.mode === GameModeEnum.SIMULATION) {
            const player1 = {
              userId: "player1",
              score: percentagesLoc[0],
            };
            const player2 = {
              userId: "player2",
              score: percentagesLoc[1],
            };
            const player1Wins = percentagesLoc[0] > percentagesLoc[1];
            const winnerLoser = {
              winner: player1Wins ? player1 : player2,
              loser: player1Wins ? player2 : player1,
            };
            dispatch(
              setGame({
                ...game,
                state: GameStateEnum.FINISHED,
                status: GameStatusEnum.RESULTS,
                ...winnerLoser,
              }),
            );

            // Track simulation count in Firestore
            const today = new Date().toISOString().split("T")[0];
            const simStatsRef = doc(db, "stats", "dailySimulations");
            setDoc(
              simStatsRef,
              { [today]: increment(1) },
              { merge: true },
            ).catch((err) => console.error("Failed to track simulation:", err));

            // Log individual simulation (read by getSimulationHistory in admin)
            addDoc(collection(db, "simulations"), {
              timestamp: serverTimestamp(),
              uid: userUid || null,
              email: getAuth().currentUser?.email || null,
              blueTeam: userCharacters, // 5 champion ids (blue/player1)
              redTeam: opponentCharacters, // 5 champion ids (red/player2)
              winProbability: percentagesLoc[0], // blue win %
            }).catch((err) => console.error("Failed to log simulation:", err));
          }

          // Only update elo if the user is ACTUALLY in the game
          // only the winner is activating the update
          // Currently we always order the players in the same way
          const userWins = jsonData.predicted_winner === 1;
          if (!userIsNotInGame && userWins && !game?.winner) {
            const winnerAndLoser = {
              winner: {
                userId: userUid,
                score: percentagesLoc[0],
              },
              loser: {
                userId: opponentId,
                score: percentagesLoc[1],
              },
            };

            if (game.mode !== GameModeEnum.SIMULATION) {
              await updateDoc(doc(db, "games", game.id), {
                state: GameStateEnum.FINISHED,
                status: GameStatusEnum.RESULTS,
                ...winnerAndLoser,
              });
              await updateDoc(doc(db, "lobbies", lobby.id), {
                state: GameStateEnum.FINISHED,
                status: GameStatusEnum.RESULTS,
                ...winnerAndLoser,
              });
            }
          }
        } catch (error) {
          console.error("Error:", error);

          // Check for auth/quota errors from Cloud Function
          const errorCode = error?.code;
          if (errorCode === "functions/unauthenticated") {
            setAuthError(true);
            return;
          }
          if (errorCode === "functions/permission-denied") {
            setEmailNotVerified(true);
            return;
          }
          if (errorCode === "functions/resource-exhausted") {
            setQuotaExhausted(true);
            return;
          }

          if (game.mode === GameModeEnum.SIMULATION) {
            dispatch(
              setGame({
                ...game,
                state: GameStateEnum.FINISHED,
                status: GameStatusEnum.RESULTS_ERROR,
              }),
            );
          } else {
            await updateDoc(doc(db, "games", game.id), {
              state: GameStateEnum.FINISHED,
              status: GameStatusEnum.RESULTS_ERROR,
            });
            await updateDoc(doc(db, "lobbies", lobby.id), {
              state: GameStateEnum.FINISHED,
              status: GameStatusEnum.RESULTS_ERROR,
            });
          }
        }
      }
    };

    fetchWinner();
  }, [retryCount, game?.winner]);

  const losingMessages = [
    "Missed the clock, now your Nexus will rock!",
    "Time’s up! Guess you're stuck with a default pick!",
    "Ran out of time, now face your draft crime!",
    "Took too long, now your comp might be wrong!",
    "Seconds away, and now you'll pay in the fray!",
    "Too slow in champ select, prepare for the wreck!",
    "The sands of time ran out, hope you brought a stout champ out!",
    "No Zilean on your side, draft delay, game astray!",
    "You let the timer end, now it's a rough Rift you’ll ascend!",
    "Draft delay, now the Rift will make you pay!",
  ];

  const winningMessages = [
    "Your draft lag is my victory flag!",
    "Thanks for the extra time, now my comp will shine!",
    "You missed your pick, now my team's looking slick!",
    "Time wasn't your friend, now it's a win I'll commend!",
    "While you were hesitating, I was champ celebrating!",
    "Your draft delay, gave my team the better play!",
    "Thanks for the timer slack, now we'll attack with a stronger pack!",
    "Time betrayed your side, for us, it’s a victorious ride!",
    "You stalled in champ select, our win you indirectly elect!",
    "Clock ran out on you, to victory, my team will now pursue!",
  ];

  const surrenderMessages = [
    "Your surrender is my encore, time for my victory tour!",
    "You hit /ff, I'll take that win, GG and better luck within!",
    "Thanks for the swift end, onto the next, my friend!",
    "Your white flag in the air, means my team's beyond compare!",
    "You tapped out, so I'll give a victory shout!",
    "Surrender at 20? We were just getting comfy!",
    "Thanks for the short game, victory's still the same!",
    "You threw in the towel, now our win roars so loud!",
    "Quick surrender, quick win, can't wait to play again!",
    "Your early /ff, is a gift to me, thanks for the quick spree!",
  ];

  const apiErrorMessages = [
    "Oops! Looks like our champions decided to dodge this draft for real. Try again!",
    "Summoners, we've misplaced the Rift! Give us a moment.",
    "Someone spilled poro-snax on the server. BRB!",
    "Even summoning platforms have off days. Stand by!",
    "Our champions went on a coffee break, just like you'd want to!",
    "Looks like Teemo blinded our servers. Bear with us!",
    "Yasuo wind-walled our connection! Trying to reconnect...",
    "Seems Bard took us on a magical journey... to a crash screen.",
    "Looks like Ornn's forging messed with our wires. We're fixing it!",
    "Thresh just lanterned us out of the game. Hang tight!",
    "Zoe's sleepy trouble bubble hit the servers. Wake up soon!",
  ];

  const getRandomWinLoseMessage = useCallback((isWin) => {
    const messages = isWin ? winningMessages : losingMessages;
    const randomIndex = Math.floor(Math.random() * messages.length);
    return messages[randomIndex];
  }, []);

  const getRandomApiErrorMessage = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * apiErrorMessages.length);
    return apiErrorMessages[randomIndex];
  }, []);

  const getRandomSurrenderMessage = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * surrenderMessages.length);
    return surrenderMessages[randomIndex];
  }, []);

  function loaderFeedback() {
    // Login required — user is not authenticated
    if (authError) {
      return (
        <div className="waitingResult">
          <p style={{ width: "70%", fontSize: "1.5rem", textAlign: "center" }}>
            Sign in to see your draft analysis
          </p>
          <div className="loginGate">
            <Login message="Sign in to unlock results" />
          </div>
          <p
            style={{
              width: "70%",
              fontSize: "1.1rem",
              opacity: 0.6,
              marginTop: "0.5rem",
              textAlign: "center",
            }}
          >
            Free accounts get {FREE_DAILY_LIMIT} analyses per day
          </p>
        </div>
      );
    }

    // Email not verified
    if (emailNotVerified) {
      return (
        <div className="waitingResult">
          <p style={{ width: "70%", fontSize: "1.5rem", textAlign: "center" }}>
            Verify your email
          </p>
          <p
            style={{
              width: "70%",
              fontSize: "1.1rem",
              opacity: 0.8,
              textAlign: "center",
            }}
          >
            Check your inbox for a verification link, then reload this page.
          </p>
          <section className="buttonContainer">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                setEmailNotVerified(false);
                setRetryCount((c) => c + 1);
              }}
              className="homeButton"
            >
              I VERIFIED — RETRY
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={leaveGame}
              className="homeButton"
            >
              HOME
            </motion.button>
          </section>
        </div>
      );
    }

    // Daily quota exhausted
    if (quotaExhausted) {
      return (
        <div className="waitingResult">
          <p style={{ width: "70%", fontSize: "1.5rem" }}>
            Daily limit reached
          </p>
          <p style={{ width: "70%", fontSize: "1rem", opacity: 0.8 }}>
            You have used all {FREE_DAILY_LIMIT} free analyses for today. Come
            back tomorrow or upgrade to Pro for unlimited access.
          </p>
          {showAds() && (
            <Adsense
              client={AdsenseAccount}
              slot={isMobile ? "5279432204" : "9863584592"}
              style={{
                display: "inline-block",
                marginTop: "1.5em",
                marginBottom: "1em",
                width: isMobile ? "300px" : "728px",
                height: isMobile ? "100px" : "90px",
              }}
              format={isMobile ? "" : "horizontal"}
            />
          )}
          <section className="buttonContainer">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={openCheckout}
              className="homeButton"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              UPGRADE TO PRO — 4.99€/mo
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={leaveGame}
              className="homeButton"
            >
              HOME
            </motion.button>
          </section>
        </div>
      );
    }

    if (game.winner !== null) {
      if (
        game.status === GameStatusEnum.MISSED_TIMER ||
        game.status === GameStatusEnum.RESULTS_ERROR ||
        game.status === GameStatusEnum.SURREND
      ) {
        return (
          <>
            <div className="timeoutMessage">
              {game.status === GameStatusEnum.MISSED_TIMER &&
                getRandomWinLoseMessage(game?.winner.userId === userUid)}
              {game.status === GameStatusEnum.getRandomApiErrorMessage &&
                getRandomApiErrorMessage(game?.winner.userId === userUid)}
              {game.status === GameStatusEnum.SURREND &&
                getRandomSurrenderMessage(game?.winner.userId === userUid)}
            </div>
            <section className="buttonContainer">
              <ShareButton
                resultData={draftereyeData}
                userTeam={userCharacters}
                opponentTeam={opponentCharacters}
                percentages={percentages}
                opponentUsername={cardOpponentUsername}
                userUsername={cardUserUsername}
                isUserBlueSide={isUserBlueSide}
              />

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={leaveGame}
                className="homeButton"
              >
                HOME
              </motion.button>

              <DraftCardButton
                userCharacters={userCharacters}
                opponentCharacters={opponentCharacters}
                userUsername={cardUserUsername}
                opponentUsername={cardOpponentUsername}
                percentages={percentages}
                isUserBlueSide={isUserBlueSide}
              />
            </section>
          </>
        );
      }

      return (
        <>
          <FeedbackDisplay
            resultData={draftereyeData}
            champions={allCharacters}
            blueSideChampions={blueSideTeam}
          />
          {showAds() && (
            <Adsense
              client={AdsenseAccount}
              slot={isMobile ? "5279432204" : "9863584592"}
              style={{
                display: "inline-block",
                marginTop: "1.5em",
                marginBottom: "1em",
                width: isMobile ? "300px" : "100%",
                height: isMobile ? "100px" : "90px",
              }}
              format={isMobile ? "" : "horizontal"}
            />
          )}
          {PRO_FEATURES_ENABLED && user && !isProOrHigher(user) && (
            <div className="usageReminder">
              <span>
                {user.dailyApiCalls || 0}/{FREE_DAILY_LIMIT} free analyses used
                today
              </span>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={openCheckout}
                className="usageUpgradeBtn"
              >
                Go Pro — Unlimited
              </motion.button>
            </div>
          )}
          {isSimulation && onEditChampions && (
            <section className="changeChampionsContainer">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onEditChampions}
                className="changeChampionsButton"
              >
                Change champions?
              </motion.button>
            </section>
          )}
          <section className="buttonContainer">
            <ShareButton
              resultData={draftereyeData}
              userTeam={userCharacters}
              opponentTeam={opponentCharacters}
              percentages={percentages}
              opponentUsername={cardOpponentUsername}
              userUsername={cardUserUsername}
              isUserBlueSide={isUserBlueSide}
            />

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={leaveGame}
              className="homeButton"
            >
              HOME
            </motion.button>

            <DraftCardButton
              userCharacters={userCharacters}
              opponentCharacters={opponentCharacters}
              userUsername={cardUserUsername}
              opponentUsername={cardOpponentUsername}
              percentages={percentages}
              isUserBlueSide={isUserBlueSide}
            />
          </section>
        </>
      );
    }
    return (
      <div className="waitingResult">
        <Audio
          height="100"
          width="100"
          color="#cc3032"
          ariaLabel="audio-loading"
          wrapperStyle={{}}
          wrapperClass="wrapper-class"
        />

        <p style={{ width: "70%" }}>Calculating results...</p>
      </div>
    );
  }
  return <div className="Results">{loaderFeedback()}</div>;
}

export default Results;
